import { z } from 'zod';

import {
  AUTO_APPROVE_CUSTOM_PROMPT_MAX_LENGTH,
  AUTO_ULTRAREVIEW_CUSTOM_PROMPT_MAX_LENGTH,
  CODING_AGENT_INSTRUCTIONS_MAX_LENGTH,
  CUSTOM_INSTRUCTIONS_MAX_LENGTH,
  MAX_CHANGED_LINES_LIMIT,
  SUMMARY_INSTRUCTIONS_MAX_LENGTH,
  VERIFY_INSTRUCTIONS_MAX_LENGTH,
} from './constants';

import {
  AutoFixLoopMode as AutoFixLoopModeEnum,
  AutoFixProvider as AutoFixProviderEnum,
  VerifyMode as VerifyModeEnum,
} from '@/generated/prisma/enums';

export const GlobPatternSchema = z.object({
  id: z.string(),
  pattern: z.string(),
});

export const LabelPatternSchema = z.object({
  id: z.string(),
  label: z.string(),
});

export const DocumentationLinkSchema = z.object({
  id: z.string(),
  libraryName: z.string(),
  version: z.string(),
  url: z.string(),
});

export const SensitivitySchema = z.enum(['low', 'medium', 'high']);

export const AUTO_APPROVE_BEHAVIORS = ['disabled', 'shadow', 'live'] as const;
export const AutoApproveBehaviorSchema = z.enum(AUTO_APPROVE_BEHAVIORS);
export type AutoApproveBehavior = z.infer<typeof AutoApproveBehaviorSchema>;

export const AUTO_APPROVE_MODES = ['disabled', 'always', 'low_risk_only', 'custom'] as const;
export const AutoApproveModeSchema = z.enum(AUTO_APPROVE_MODES);
export type AutoApproveMode = z.infer<typeof AutoApproveModeSchema>;

/**
 * Open cubic findings that auto-approval tolerates, by priority. Each range starts
 * at P3: allowing a higher priority while blocking a lower one has no use.
 */
export const AUTO_APPROVE_ALLOWED_OPEN_ISSUES = [
  'none',
  'p3',
  'p2_and_p3',
  'p1_to_p3',
  'all',
] as const;
export const AutoApproveAllowedOpenIssuesSchema = z.enum(AUTO_APPROVE_ALLOWED_OPEN_ISSUES);
export type AutoApproveAllowedOpenIssues = z.infer<typeof AutoApproveAllowedOpenIssuesSchema>;

/**
 * PR metadata that deterministic auto-approval filters can gate on.
 *
 * Changed-file rules stay in the sibling auto-approval pattern settings because
 * they need a GitHub fetch and per-path matching. These attributes are already
 * in hand when the approval decision runs.
 */
export const AUTO_APPROVE_FILTER_ATTRIBUTES = [
  'author',
  'head_branch',
  'base_branch',
  'label',
  'title',
] as const;
export const AutoApproveFilterAttributeSchema = z.enum(AUTO_APPROVE_FILTER_ATTRIBUTES);
export type AutoApproveFilterAttribute = z.infer<typeof AutoApproveFilterAttributeSchema>;

export const AUTO_APPROVE_FILTER_OPERATORS = ['include', 'exclude'] as const;
export const AutoApproveFilterOperatorSchema = z.enum(AUTO_APPROVE_FILTER_OPERATORS);
export type AutoApproveFilterOperator = z.infer<typeof AutoApproveFilterOperatorSchema>;

export const AUTO_APPROVE_FILTER_VALUE_MAX_LENGTH = 256;

export const AutoApproveFilterSchema = z.object({
  id: z.string(),
  attribute: AutoApproveFilterAttributeSchema,
  operator: AutoApproveFilterOperatorSchema,
  value: z.string().min(1).max(AUTO_APPROVE_FILTER_VALUE_MAX_LENGTH),
});
export type AutoApproveFilter = z.infer<typeof AutoApproveFilterSchema>;

export const AUTO_ULTRAREVIEW_MODES = ['disabled', 'high_risk_only', 'custom'] as const;
export const AutoUltrareviewModeSchema = z.enum(AUTO_ULTRAREVIEW_MODES);
export type AutoUltrareviewMode = z.infer<typeof AutoUltrareviewModeSchema>;

export const ULTRAREVIEW_MODES = ['disabled', 'manual', 'automatic'] as const;
export const UltrareviewModeSchema = z.enum(ULTRAREVIEW_MODES);
export type UltrareviewMode = z.infer<typeof UltrareviewModeSchema>;

export const AutoFixProviderSchema = z.nativeEnum(AutoFixProviderEnum);
export type AutoFixProvider = z.infer<typeof AutoFixProviderSchema>;
export const DEFAULT_AUTO_FIX_PROVIDER: AutoFixProvider = AutoFixProviderEnum.cubic;

/**
 * Whether the auto-fix loop is offered in a repository: not at all
 * (`disabled`), turned on per pull request by a person (`manual`), or turned on
 * by itself for every pull request that `autoFixLoopFilters` lets through
 * (`automatic`). A person can still turn it off on any one pull request.
 */
export const AutoFixLoopModeSchema = z.nativeEnum(AutoFixLoopModeEnum);
export type AutoFixLoopMode = z.infer<typeof AutoFixLoopModeSchema>;
export const DEFAULT_AUTO_FIX_LOOP_MODE: AutoFixLoopMode = AutoFixLoopModeEnum.on_demand;

/**
 * Whether cubic films a demo of each pull request that changes the UI of a
 * repository (`on`), narrowed by `verifyFilters`, or of none (`off`). Verify is
 * internal: only installations that can use cubic code read it.
 */
export const VerifyModeSchema = z.nativeEnum(VerifyModeEnum);
export type VerifyMode = z.infer<typeof VerifyModeSchema>;
export const DEFAULT_VERIFY_MODE: VerifyMode = VerifyModeEnum.on;

export const YamlSettingOriginSchema = z.enum(['repo_yaml', 'org_yaml']);

const MaxChangedLinesValueSchema = z.number().int().positive().max(MAX_CHANGED_LINES_LIMIT);

export const AiReviewSettingsResponseSchema = z.object({
  installationId: z.number(),
  repositoryId: z.number(),
  generalReviewEnabled: z.boolean(),
  customInstructions: z.string().nullable().optional(),
  documentationLinks: DocumentationLinkSchema.array().nullable().optional(),
  ignorePatterns: GlobPatternSchema.array().nullable().optional(),
  includePatterns: GlobPatternSchema.array().nullable().optional(),
  autoApproveExcludePatterns: GlobPatternSchema.array().nullable().optional(),
  // default(null) keeps pre-deploy KV-cached settings objects parseable without
  // making the application-facing setting optional.
  autoApproveIncludePatterns: GlobPatternSchema.array().nullable().default(null),
  // nullable().optional() keeps pre-deploy KV-cached settings objects parseable
  autoApproveFilters: AutoApproveFilterSchema.array().nullable().optional(),
  ignoreBranchPatterns: GlobPatternSchema.array().nullable().optional(),
  ignoreBaseBranchPatterns: GlobPatternSchema.array().nullable().optional(),
  ignoreLabels: LabelPatternSchema.array().nullable().optional(),
  ignoreTitlePatterns: GlobPatternSchema.array().nullable().optional(),
  // default(null) keeps pre-deploy KV-cached settings objects parseable
  maxChangedLines: MaxChangedLinesValueSchema.nullable().default(null),
  checkDrafts: z.boolean(),
  generatePrDescriptionEnabled: z.boolean(),
  prDescriptionReviewLinkEnabled: z.boolean(),
  // default(false) keeps pre-deploy KV-cached settings objects parseable
  skipPrDescriptionIfAuthorProvided: z.boolean().default(false),
  incrementalReviewsEnabled: z.boolean(),
  architectureDiagramsEnabled: z.boolean(),
  externalContributorsRequireManualReview: z.boolean(),
  sensitivity: SensitivitySchema,
  autoApproveBehavior: AutoApproveBehaviorSchema,
  autoApproveMode: AutoApproveModeSchema,
  autoApproveCustomPrompt: z.string().nullable().optional(),
  // default(true) keeps pre-deploy KV-cached settings objects parseable, with the column's default
  autoApproveExcludeExternalContributors: z.boolean().default(true),
  // default('none') keeps pre-deploy KV-cached settings objects parseable
  autoApproveAllowedOpenIssues: AutoApproveAllowedOpenIssuesSchema.default('none'),
  // default('manual') keeps pre-deploy KV-cached settings objects parseable
  ultrareviewMode: UltrareviewModeSchema.default('manual'),
  // default('disabled') keeps pre-deploy KV-cached settings objects parseable
  autoUltrareviewMode: AutoUltrareviewModeSchema.default('disabled'),
  autoUltrareviewCustomPrompt: z.string().nullable().optional(),
  autoUltrareviewFilePatterns: GlobPatternSchema.array().nullable().optional(),
  mergeConfidenceEnabled: z.boolean(),
  summaryInstructions: z.string().nullable().optional(),
  autoFixButtonEnabled: z.boolean(),
  prCommentFixesEnabled: z.boolean(),
  fixCommitsToPr: z.boolean(),
  autoFixProvider: AutoFixProviderSchema,
  // default('on_demand') keeps pre-deploy KV-cached settings objects parseable
  autoFixLoopMode: AutoFixLoopModeSchema.default(DEFAULT_AUTO_FIX_LOOP_MODE),
  // Same row shape as autoApproveFilters; null or empty lets every pull request through.
  autoFixLoopFilters: AutoApproveFilterSchema.array().nullable().optional(),
  // default('on') keeps pre-deploy KV-cached settings objects parseable
  verifyMode: VerifyModeSchema.default(DEFAULT_VERIFY_MODE),
  // Same row shape as autoApproveFilters; null or empty lets every pull request through.
  verifyFilters: AutoApproveFilterSchema.array().nullable().optional(),
  // default(null) keeps pre-deploy KV-cached settings objects parseable without
  // making the application-facing setting optional.
  verifyInstructions: z.string().nullable().default(null),
  // nullable().optional() keeps pre-deploy KV-cached settings objects parseable
  codingAgentInstructions: z.string().nullable().optional(),
  resolveThreadsWhenAddressed: z.boolean(),
  yamlSettingOrigins: z.record(z.string(), YamlSettingOriginSchema).optional(),
});

const PatternUpdateSchema = z.union([GlobPatternSchema.array(), z.null()]).optional();

const LabelPatternUpdateSchema = z.union([LabelPatternSchema.array(), z.null()]).optional();

const AutoApproveFilterUpdateSchema = z
  .union([AutoApproveFilterSchema.array(), z.null()])
  .optional();

export const AiReviewSettingsUpdateSchema = z.object({
  installationId: z.number(),
  repositoryId: z.number(),
  generalReviewEnabled: z.boolean().optional(),
  customInstructions: z.string().max(CUSTOM_INSTRUCTIONS_MAX_LENGTH).nullable().optional(),
  summaryInstructions: z.string().max(SUMMARY_INSTRUCTIONS_MAX_LENGTH).nullable().optional(),
  documentationLinks: DocumentationLinkSchema.array().nullable().optional(),
  ignorePatterns: PatternUpdateSchema,
  includePatterns: PatternUpdateSchema,
  autoApproveExcludePatterns: PatternUpdateSchema,
  autoApproveIncludePatterns: PatternUpdateSchema,
  autoApproveFilters: AutoApproveFilterUpdateSchema,
  ignoreBranchPatterns: PatternUpdateSchema,
  ignoreBaseBranchPatterns: PatternUpdateSchema,
  ignoreLabels: LabelPatternUpdateSchema,
  ignoreTitlePatterns: PatternUpdateSchema,
  maxChangedLines: MaxChangedLinesValueSchema.nullable().optional(),
  checkDrafts: z.boolean().optional(),
  generatePrDescriptionEnabled: z.boolean().optional(),
  prDescriptionReviewLinkEnabled: z.boolean().optional(),
  skipPrDescriptionIfAuthorProvided: z.boolean().optional(),
  incrementalReviewsEnabled: z.boolean().optional(),
  architectureDiagramsEnabled: z.boolean().optional(),
  externalContributorsRequireManualReview: z.boolean().optional(),
  sensitivity: SensitivitySchema.optional(),
  autoApproveBehavior: AutoApproveBehaviorSchema.optional(),
  autoApproveMode: AutoApproveModeSchema.optional(),
  autoApproveCustomPrompt: z
    .string()
    .max(AUTO_APPROVE_CUSTOM_PROMPT_MAX_LENGTH)
    .nullable()
    .optional(),
  autoApproveExcludeExternalContributors: z.boolean().optional(),
  autoApproveAllowedOpenIssues: AutoApproveAllowedOpenIssuesSchema.optional(),
  ultrareviewMode: UltrareviewModeSchema.optional(),
  autoUltrareviewMode: AutoUltrareviewModeSchema.optional(),
  autoUltrareviewCustomPrompt: z
    .string()
    .max(AUTO_ULTRAREVIEW_CUSTOM_PROMPT_MAX_LENGTH)
    .nullable()
    .optional(),
  autoUltrareviewFilePatterns: PatternUpdateSchema,
  mergeConfidenceEnabled: z.boolean().optional(),
  autoFixButtonEnabled: z.boolean().optional(),
  prCommentFixesEnabled: z.boolean().optional(),
  fixCommitsToPr: z.boolean().optional(),
  autoFixProvider: AutoFixProviderSchema.optional(),
  autoFixLoopMode: AutoFixLoopModeSchema.optional(),
  autoFixLoopFilters: AutoApproveFilterUpdateSchema,
  verifyMode: VerifyModeSchema.optional(),
  verifyFilters: AutoApproveFilterUpdateSchema,
  verifyInstructions: z.string().max(VERIFY_INSTRUCTIONS_MAX_LENGTH).nullable().optional(),
  codingAgentInstructions: z
    .string()
    .max(CODING_AGENT_INSTRUCTIONS_MAX_LENGTH)
    .nullable()
    .optional(),
  resolveThreadsWhenAddressed: z.boolean().optional(),
});

export type GlobPattern = z.infer<typeof GlobPatternSchema>;
export type LabelPattern = z.infer<typeof LabelPatternSchema>;
export type DocumentationLink = z.infer<typeof DocumentationLinkSchema>;
export type AiReviewSettingsResponse = z.infer<typeof AiReviewSettingsResponseSchema>;
export type AiReviewSettingsUpdateInput = z.infer<typeof AiReviewSettingsUpdateSchema>;
